import React from "react";
import { useNavigate } from "react-router-dom";
import StageQuestionsView from "./StageQuestionsView";
import StageEditorView from "./StageEditorView";
import StageDocumentView from "./StageDocumentView";
import { Layers } from "lucide-react";
import { showToast as toast } from "../../../../components/ClassicToast";

/**
 * Dynamically resolves and renders the appropriate stage component
 * based on stage.contentType:
 * - 'QUESTIONS' -> StageQuestionsView
 * - 'EDITOR'    -> StageEditorView
 * - 'DOCUMENT'  -> StageDocumentView
 */
export default function DynamicStageContent({
  stage,
  project,
  isCompleted,
  onCompleteStage,
  onNextStage,
  isLastStage: isLastStageProp,
  stages,
}) {
  const navigate = useNavigate();

  if (!stage) {
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "var(--mute)" }}>
        <Layers size={28} style={{ margin: "0 auto 8px" }} />
        <p style={{ fontSize: "13.5px" }}>Select a stage above to view its contents.</p>
      </div>
    );
  }

  const allStages = stages || project?.stages || [];
  const isLastStage =
    typeof isLastStageProp === "boolean"
      ? isLastStageProp
      : allStages.length > 0 &&
        (allStages[allStages.length - 1]?.id === stage?.id ||
          stage?.sequenceNo === Math.max(...allStages.map((s) => s.sequenceNo || 0)));

  const handleNextStage = () => {
    if (isLastStage) {
      if (onCompleteStage) onCompleteStage();
      toast.success("Congratulations! You have completed all stages of this project.");
      navigate("/student-stem/projects");
    } else {
      if (onNextStage) onNextStage();
    }
  };

  const contentType = (stage.contentType || "").toUpperCase();

  switch (contentType) {
    case "QUESTIONS":
      return (
        <StageQuestionsView
          stage={stage}
          project={project}
          isCompleted={isCompleted}
          onCompleteStage={onCompleteStage}
          onNextStage={handleNextStage}
          isLastStage={isLastStage}
        />
      );

    case "EDITOR":
      return (
        <StageEditorView
          stage={stage}
          project={project}
          isCompleted={isCompleted}
          onCompleteStage={onCompleteStage}
          onNextStage={handleNextStage}
          isLastStage={isLastStage}
        />
      );

    case "DOCUMENT":
      return (
        <StageDocumentView
          stage={stage}
          project={project}
          isCompleted={isCompleted}
          onCompleteStage={onCompleteStage}
          onNextStage={handleNextStage}
          isLastStage={isLastStage}
        />
      );

    default:
      return (
        <div style={{ padding: "20px", background: "var(--bg)", borderRadius: "10px", border: "1px solid var(--line)" }}>
          <h4 style={{ fontSize: "14px", fontWeight: "700", color: "var(--ink)", margin: "0 0 6px" }}>
            {stage.name}
          </h4>
          <p style={{ fontSize: "13px", color: "var(--mute)", margin: 0 }}>
            {stage.description || "Stage content will be available soon."}
          </p>
        </div>
      );
  }
}
